import classNames from 'classnames'
import { useState } from 'react'
import { Button } from 'src/modules/core/components'
import {
  describeClientPhones,
  formatInRecordZone,
  formatLeadTime,
  LATE_CANCEL_DATE_FORMATS,
} from '../../utils/late-cancel-display.util'
import type { LateCancelCellProps, LateCancelRowProps } from './late-cancel-row.interface'
import classes from './late-cancel-row.module.scss'

function ClientCell({ record }: LateCancelCellProps) {
  const phones = describeClientPhones(record.client_phones)
  return (
    <div role="cell" className={classes.cell} data-label="Client">
      <span className={classes.primary}>{record.client_name || 'No name'}</span>
      {phones.first && (
        <span>
          {phones.first}
          {phones.extraCount > 0 && <span className={classes.extra}>{`+${phones.extraCount}`}</span>}
        </span>
      )}
      {record.client_email ? (
        <span className={classes.muted}>{record.client_email}</span>
      ) : (
        <span className={classes.placeholder}>No email</span>
      )}
    </div>
  )
}

function AppointmentCell({ record }: LateCancelCellProps) {
  const start = formatInRecordZone(
    record.appointment_start_at,
    record.display_timezone,
    LATE_CANCEL_DATE_FORMATS.appointment,
  )
  return (
    <div role="cell" className={classes.cell} data-label="Appointment">
      <span className={classNames(classes.typeName, { [classes.muted]: !record.appointment_type_name })}>
        {record.appointment_type_name || 'Calendar appointment'}
      </span>
      <span>{start}</span>
    </div>
  )
}

function CancelledCell({ record }: LateCancelCellProps) {
  if (record.channel === 'owner') {
    const markedOn = formatInRecordZone(
      record.occurred_at,
      record.display_timezone,
      LATE_CANCEL_DATE_FORMATS.ownerMarked,
    )
    return (
      <div role="cell" className={classes.cell} data-label="Cancelled">
        <span>{`Marked by you on ${markedOn}`}</span>
      </div>
    )
  }
  return (
    <div role="cell" className={classes.cell} data-label="Cancelled">
      <span>{formatInRecordZone(record.occurred_at, record.display_timezone, LATE_CANCEL_DATE_FORMATS.cancelled)}</span>
      <span className={classes.chip}>{formatLeadTime(record)}</span>
    </div>
  )
}

function PolicyCell({ record }: LateCancelCellProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const text = record.policy_text?.trim()
  return (
    <div role="cell" className={classes.cell} data-label="Policy">
      {text ? (
        <button
          type="button"
          className={classes.linkButton}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((current) => !current)}
        >
          {isExpanded ? 'Hide policy' : 'Show policy'}
        </button>
      ) : (
        <span className={classes.placeholder}>No policy text</span>
      )}
      {text && isExpanded && <p className={classes.policyText}>{text}</p>}
    </div>
  )
}

function ActionCell({ record, isPending, onResolve, onUndo }: LateCancelRowProps) {
  if (!record.resolved_at) {
    return (
      <div role="cell" className={classes.actionCell}>
        <Button
          variant="withBorder"
          className={classes.resolveButton}
          disabled={isPending}
          onClick={() => onResolve(record)}
        >
          Mark resolved
        </Button>
      </div>
    )
  }
  const resolvedOn = formatInRecordZone(record.resolved_at, record.display_timezone, LATE_CANCEL_DATE_FORMATS.resolved)
  return (
    <div role="cell" className={classes.actionCell}>
      <span className={classes.muted}>{`Resolved ${resolvedOn}`}</span>
      <button type="button" className={classes.linkButton} disabled={isPending} onClick={() => onUndo(record)}>
        Undo
      </button>
    </div>
  )
}

export function LateCancelRow(props: LateCancelRowProps) {
  const { record, className } = props
  return (
    <div role="row" className={className} data-testid="late-cancel-row">
      <ClientCell record={record} />
      <AppointmentCell record={record} />
      <CancelledCell record={record} />
      <PolicyCell record={record} />
      <ActionCell {...props} />
    </div>
  )
}
